import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView, TextInput } from 'react-native';
import { Button } from 'react-native-elements';
import { colors, typography, spacing } from '../../styles/globalStyles';
import * as Speech from 'expo-speech';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';

const THEMES = [
  { id: 'fantasy', name: 'Fantasy', icon: '🏰' },
  { id: 'mystery', name: 'Mystery', icon: '🔍' },
  { id: 'scifi', name: 'Sci-Fi', icon: '🚀' },
  { id: 'adventure', name: 'Adventure', icon: '🗺️' }
];

const STORY_STARTERS = {
  fantasy: [
    "In a magical forest where trees whispered secrets...",
    "The young wizard discovered an ancient spell book...",
    "Dragons had returned to the kingdom after a thousand years..."
  ],
  mystery: [
    "The old mansion held a peculiar secret...",
    "Detective Smith found an unusual clue at the scene...",
    "The mysterious package arrived exactly at midnight..."
  ],
  scifi: [
    "The spaceship's emergency lights flashed red...",
    "Scientists discovered a signal from deep space...",
    "The robot developed an unexpected ability..."
  ],
  adventure: [
    "The treasure map led to an unexpected location...",
    "Deep in the jungle, they found ancient ruins...",
    "The storm forced their ship off course..."
  ]
};

export default function StoryChaosGame() {
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [storyStarted, setStoryStarted] = useState(false);
  const [currentStory, setCurrentStory] = useState<string[]>([]);
  const [newSentence, setNewSentence] = useState('');
  const { user } = useAuth();
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<number>(0);

  const startGameSession = async (themeId: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('game_sessions')
        .insert({
          user_id: user.id,
          game_type: 'story_chaos',
          theme: themeId,
          story_length: 0,
          duration_seconds: 0,
          completed: false,
          story_content: [],
          session_data: {
            theme: themeId,
            storyStarted: true
          }
        })
        .select()
        .single();

      if (error) throw error;
      setSessionId(data.id);
      setStartTime(Date.now());
    } catch (error) {
      console.error('Error starting game session:', error);
    }
  };

  const updateGameSession = async () => {
    if (!sessionId || !user) return;

    try {
      const duration = Math.floor((Date.now() - startTime) / 1000);
      
      await supabase
        .from('game_sessions')
        .update({
          story_length: currentStory.length,
          duration_seconds: duration,
          story_content: currentStory,
          session_data: {
            theme: selectedTheme,
            storyStarted: true,
            lastUpdated: new Date().toISOString()
          }
        })
        .eq('id', sessionId);
    } catch (error) {
      console.error('Error updating game session:', error);
    }
  };

  const completeGameSession = async () => {
    if (!sessionId || !user) return;

    try {
      const duration = Math.floor((Date.now() - startTime) / 1000);
      
      await supabase
        .from('game_sessions')
        .update({
          completed: true,
          duration_seconds: duration,
          story_content: currentStory,
          session_data: {
            theme: selectedTheme,
            storyStarted: true,
            completed: true,
            lastUpdated: new Date().toISOString()
          }
        })
        .eq('id', sessionId);
    } catch (error) {
      console.error('Error completing game session:', error);
    }
  };

  useEffect(() => {
    if (currentStory.length > 0) {
      updateGameSession();
    }
  }, [currentStory]);

  const handleThemeSelect = async (themeId: string) => {
    setSelectedTheme(themeId);
    const starters = STORY_STARTERS[themeId as keyof typeof STORY_STARTERS];
    const randomStarter = starters[Math.floor(Math.random() * starters.length)];
    setCurrentStory([randomStarter]);
    setStoryStarted(true);
    await startGameSession(themeId);
  };

  const handleAddSentence = () => {
    if (newSentence.trim()) {
      setCurrentStory([...currentStory, newSentence.trim()]);
      setNewSentence('');
    }
  };

  const readStory = () => {
    const fullStory = currentStory.join(' ');
    Speech.speak(fullStory, {
      language: 'en',
      pitch: 1,
      rate: 0.8,
    });
  };

  const handleComplete = async () => {
    await completeGameSession();
    // Navigate back or show completion screen
  };

  if (!storyStarted) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Choose Your Story Theme</Text>
        <View style={styles.themesContainer}>
          {THEMES.map((theme) => (
            <TouchableOpacity
              key={theme.id}
              style={styles.themeButton}
              onPress={() => handleThemeSelect(theme.id)}
            >
              <Text style={styles.themeIcon}>{theme.icon}</Text>
              <Text style={styles.themeName}>{theme.name}</Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <ScrollView style={styles.storyContainer}>
        {currentStory.map((sentence, index) => (
          <Text key={index} style={styles.storySentence}>
            {sentence}
          </Text>
        ))}
      </ScrollView>

      <View style={styles.inputContainer}>
        <TextInput
          style={styles.input}
          value={newSentence}
          onChangeText={setNewSentence}
          placeholder="Add to the story..."
          multiline
        />
        <Button
          title="Add"
          onPress={handleAddSentence}
          disabled={!newSentence.trim()}
          buttonStyle={styles.addButton}
        />
      </View>

      <View style={styles.buttonContainer}>
        <Button
          title="Read Story Aloud"
          onPress={readStory}
          buttonStyle={styles.readButton}
        />
        <Button
          title="Complete Story"
          onPress={handleComplete}
          buttonStyle={styles.completeButton}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: spacing.md,
    backgroundColor: colors.background,
  },
  title: {
    ...typography.title,
    textAlign: 'center',
    marginBottom: spacing.xl,
  },
  themesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: spacing.md,
  },
  themeButton: {
    backgroundColor: colors.surface,
    padding: spacing.md,
    borderRadius: 10,
    alignItems: 'center',
    width: '45%',
    aspectRatio: 1,
    justifyContent: 'center',
    elevation: 2,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  themeIcon: {
    fontSize: 32,
    marginBottom: spacing.sm,
  },
  themeName: {
    ...typography.subtitle,
    color: colors.text.primary,
  },
  storyContainer: {
    flex: 1,
    marginBottom: spacing.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
    borderRadius: 10,
  },
  storySentence: {
    ...typography.body,
    marginBottom: spacing.sm,
  },
  inputContainer: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginBottom: spacing.md,
  },
  input: {
    flex: 1,
    backgroundColor: colors.surface,
    padding: spacing.sm,
    borderRadius: 5,
    ...typography.body,
  },
  addButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
  },
  readButton: {
    backgroundColor: colors.secondary,
    marginTop: spacing.sm,
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: spacing.md,
  },
  completeButton: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
  },
}); 